import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {reduxForm, Field, propTypes as FormPropTypes, formValueSelector} from 'redux-form';
import block from 'bem-cn-lite';
import {FormFieldSelect, FormFieldTextArea} from '@yandex-data-ui/cloud-components';

import SpeechkitConstants from '../../../../common/speechkit/constants';
import withSpeechkitConfig from 'hoc/withSpeechkitConfig';
import {restoreRefs, forwardRefs} from 'hoc/utils';
import {parseStringValue} from 'utils';
import validate from './validate';
import withLocale from 'hoc/withLocale';
import withTranslation from 'hoc/withTranslation';

import FormLayout from 'components/forms/FormLayout/FormLayout';
import {FormFieldRangeInputPicker} from 'components/formFields';

import './SpeechKitTTSForm.scss';

const b = block('SpeechKitTTSForm');

const getLangItems = _.memoize((i18nK) => [
    {title: i18nK('lang-russian'), value: SpeechkitConstants.Language.Ru},
    {title: i18nK('lang-english'), value: SpeechkitConstants.Language.En},
    {title: i18nK('lang-turkey'), value: SpeechkitConstants.Language.Tr},
]);

const getVoiceItems = _.memoize((i18nK) => [
    {title: i18nK('voice-alena'), value: SpeechkitConstants.Voice.Alena, onlyRu: true},
    {title: i18nK('voice-filipp'), value: SpeechkitConstants.Voice.Filipp, onlyRu: true},
    {title: i18nK('voice-alyss'), value: SpeechkitConstants.Voice.Alyss},
    {title: i18nK('voice-jane'), value: SpeechkitConstants.Voice.Jane},
    {title: i18nK('voice-oksana'), value: SpeechkitConstants.Voice.Oksana},
    {title: i18nK('voice-omazh'), value: SpeechkitConstants.Voice.Omazh},
    {title: i18nK('voice-zahar'), value: SpeechkitConstants.Voice.Zahar},
    {title: i18nK('voice-ermil'), value: SpeechkitConstants.Voice.Ermil},
]);

const getEmotionItems = _.memoize((i18nK) => [
    {title: i18nK('emotion-good'), value: SpeechkitConstants.Emotion.Good},
    {title: i18nK('emotion-evil'), value: SpeechkitConstants.Emotion.Evil},
    {title: i18nK('emotion-neutral'), value: SpeechkitConstants.Emotion.Neutral},
]);

export const getInitialValuesRu = _.memoize((i18nK) => ({
    message: i18nK('tts-form-default_message'),
    language: getLangItems(i18nK)[0].value,
    speed: 1,
    voice: getVoiceItems(i18nK)[0].value,
    emotion: getEmotionItems(i18nK)[0].value,
}));

export const getInitialValuesEn = _.memoize((i18nK) => ({
    ...getInitialValuesRu(i18nK),
    language: getLangItems(i18nK)[1].value,
}));

class SpeechKitTTSForm extends React.Component {
    static propTypes = {
        ...FormPropTypes,
        locale: PropTypes.object.isRequired,
        i18nK: PropTypes.func,
        currentValues: PropTypes.object.isRequired,
    };

    componentDidMount() {
        this.initialize();
    }

    submit() {
        return this.submit();
    }

    initialize() {
        const {
            initialize,
            locale: {lang},
            i18nK,
        } = this.props;

        initialize(lang === 'ru' ? getInitialValuesRu(i18nK) : getInitialValuesEn(i18nK));
    }

    getLangItems = () => {
        const {i18nK} = this.props;

        return getLangItems(i18nK);
    };

    getVoiceItems = () => {
        const {
            currentValues: {language},
            i18nK,
        } = this.props;

        const voices = getVoiceItems(i18nK);
        const filteredVoices =
            language === SpeechkitConstants.Language.Ru ? voices : voices.filter((e) => !e.onlyRu);

        return filteredVoices;
    };

    onLanguageChange = (_, language) => {
        const {
            currentValues: {voice},
            i18nK,
        } = this.props;

        const {onlyRu} = getVoiceItems(i18nK).find(({value}) => voice === value);

        if (language !== SpeechkitConstants.Language.Ru && onlyRu) {
            const availableVoices = getVoiceItems(i18nK).filter((e) => !e.onlyRu);

            this.props.change('voice', availableVoices[0].value);
        }
    };

    getSpeedOption() {
        const minValue = 0.1;
        const maxValue = 3.00000001; // Impressive hack for RangeInputPicker/rc-slider
        const step = 0.1;

        return {
            minValue,
            maxValue,
            step,
            format: (value) => value.toFixed(1) + 'x',
            parse: (str) => parseStringValue(str),
            className: b('field-range-input-picker'),
        };
    }

    render() {
        const {initialized, i18nK} = this.props;

        if (!initialized) {
            return null;
        }

        return (
            <div className={b()}>
                <div className={b('left')}>
                    <Field
                        name="message"
                        component={FormFieldTextArea}
                        className={b('field-textarea')}
                        size="l"
                        rows={12}
                    />
                </div>
                <div className={b('right')}>
                    <FormLayout.Row name={i18nK('tts-form-language')}>
                        <Field
                            name={'language'}
                            component={FormFieldSelect}
                            size="n"
                            items={this.getLangItems()}
                            onChange={this.onLanguageChange}
                        />
                    </FormLayout.Row>
                    <FormLayout.Row name={i18nK('tts-form-speed')}>
                        <Field
                            name={'speed'}
                            component={FormFieldRangeInputPicker}
                            size="l"
                            props={this.getSpeedOption()}
                        />
                    </FormLayout.Row>
                    <FormLayout.Row name={i18nK('tts-form-voice')}>
                        <Field
                            name={'voice'}
                            component={FormFieldSelect}
                            size="n"
                            items={this.getVoiceItems(i18nK)}
                        />
                    </FormLayout.Row>
                    <FormLayout.Row name={i18nK('tts-form-emotion')}>
                        <Field
                            name={'emotion'}
                            component={FormFieldSelect}
                            size="n"
                            items={getEmotionItems(i18nK)}
                        />
                    </FormLayout.Row>
                </div>
            </div>
        );
    }
}

const formName = 'speechkit-tts-form';
const formSelector = formValueSelector(formName);

export default compose(
    forwardRefs,
    withTranslation('speechkit-demo'),
    withSpeechkitConfig,
    withLocale,
    restoreRefs,
    reduxForm({
        form: formName,
        validate,
        initialValues: {
            message: '',
        },
    }),
    connect((state) => ({
        currentValues: {
            voice: formSelector(state, 'voice'),
            language: formSelector(state, 'language'),
            speed: formSelector(state, 'speed'),
        },
    })),
)(SpeechKitTTSForm);
